import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmActionModalComponent } from '../confirm-action-modal/confirm-action-modal.component';
import type {
  FamilyStartPreview,
  FamilyStartReason,
  FamilyStartSubject,
} from '../../types/family-start.types';
import { startFamilyReasonMessage } from '../../utils/family-start.util';

/**
 * "Start Family": the membership becomes the primary of a new Family Plan. The primary gets no
 * discount and nothing is charged; only members added later do. Presentational: it makes no API
 * call. The host runs the flow (see StartFamilyFlowController)
 * and feeds `preview`, `loading`, `busy` and `reason` back. Shared by the Customers → Membership
 * tab and the lane subscription pop-ups.
 *
 * @selector app-start-family-modal
 * @input target - The membership; the modal renders while set.
 * @input preview - Whether the membership can start a family, and why not.
 * @input loading - The preview is loading.
 * @input busy - Request in flight: controls are disabled.
 * @input reason - Refusal to show inside the modal.
 * @output confirm - Start the family.
 * @output cancel - Closed without starting.
 */
@Component({
  selector: 'app-start-family-modal',
  standalone: true,
  imports: [CommonModule, ConfirmActionModalComponent],
  templateUrl: './start-family-modal.component.html',
  styleUrls: ['./start-family-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class StartFamilyModalComponent {
  @Input() target: FamilyStartSubject | null = null;
  @Input() preview: FamilyStartPreview | null = null;
  @Input() loading = false;
  @Input() busy = false;
  @Input() reason: FamilyStartReason | null = null;

  @Output() confirm = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  get canConfirm(): boolean {
    return !!this.preview?.eligible && !this.busy;
  }

  get reasonMessage(): string {
    return startFamilyReasonMessage(this.reason);
  }

  onConfirm(): void {
    if (this.canConfirm) {
      this.confirm.emit();
    }
  }
}
